import { isAnyOf } from '@reduxjs/toolkit';
import {
  applyStockAlignment,
  fetchCatalogRun,
  fetchListings,
  fetchStockAlignmentRun,
  fetchStockPreview,
  fetchStockSync,
  startCatalogSync,
  startStockPreview,
  syncListing,
  updateListing,
} from './stockSyncThunks';
import { stockPreviewExpired } from './stockSyncSlice';
import {
  PENDING_POLL_MS,
  PENDING_WATCH_MAX_MS,
  RUN_KIND,
  RUN_POLL_MS,
  RUN_WATCH_MAX_MS,
  STOCK_SYNC_NOTICE,
  STOCK_SYNC_WATCHER,
} from './stockSyncConstants';
import { hasPendingListings, isRunActive, previewTokenOf, runEndedNotice } from './stockSyncUtils';
import { fetchCatalog } from '../catalog/catalogThunks';
import { fetchStock } from '../stock/stockThunks';
import { notify } from '../feedback/notify';

/**
 * Each run, how it is read, and what the page watching reads again once it
 * ends. A comparison's own read brings its rows; an alignment marks the
 * comparison applied.
 */
const RUNS = {
  [RUN_KIND.CATALOG]: {
    start: startCatalogSync,
    read: fetchCatalogRun,
    refresh: { [STOCK_SYNC_WATCHER.PRODUCTS]: () => fetchCatalog() },
  },
  [RUN_KIND.STOCK_PREVIEW]: { start: startStockPreview, read: fetchStockPreview, refresh: {} },
  [RUN_KIND.STOCK_ALIGNMENT]: {
    start: applyStockAlignment,
    read: fetchStockAlignmentRun,
    refresh: {
      [STOCK_SYNC_WATCHER.INVENTORY]: () => fetchStock(),
      [STOCK_SYNC_WATCHER.ALIGNMENT]: (settingId) => fetchStockPreview({ settingId, background: true }),
    },
  },
};

/** A page is on the store: leaving it, or choosing another, ends what it watched. */
const isWatching = (state, settingId) =>
  state.stockSync.watchedBy !== null && state.stockSync.selectedId === settingId;

/**
 * Whether an outcome starts the watch of a run: it was just queued, or the
 * store was read while one was out — opened in the middle of one, or of the
 * nightly sync. The watch's own reads go in the background and never restart it.
 */
const startsRunWatch = (kind) => (action, state) => {
  const { start } = RUNS[kind];
  if (start.fulfilled.match(action)) return isRunActive(action.payload);
  if (fetchStockSync.fulfilled.match(action) && action.meta.arg?.background !== true) {
    return isRunActive(action.payload?.runs?.[kind]) && isWatching(state, action.payload.channel?.id);
  }
  return false;
};

const watchRun = (kind) => async (action, { dispatch, getState, delay, cancelActiveListeners }) => {
  cancelActiveListeners();
  const { read, refresh } = RUNS[kind];
  const { settingId } = action.meta.arg;
  const until = Date.now() + RUN_WATCH_MAX_MS;

  while (Date.now() < until) {
    await delay(RUN_POLL_MS);
    if (!isWatching(getState(), settingId)) return;
    const outcome = await dispatch(read({ settingId, background: true }));
    if (!isWatching(getState(), settingId)) return;
    // A read that fails is let go: the next one may answer.
    if (read.fulfilled.match(outcome) && !isRunActive(outcome.payload)) {
      dispatch(fetchStockSync({ settingId, background: true }));
      const refreshTable = refresh[getState().stockSync.watchedBy];
      if (refreshTable) dispatch(refreshTable(settingId));
      const notice = runEndedNotice(kind, outcome.payload);
      if (notice) dispatch(notify[notice.type](notice.message, { testId: STOCK_SYNC_NOTICE }));
      return;
    }
  }
};

/**
 * What a person's own action leaves pending on the page: a push asked by
 * hand, a product resumed, or a page read with some already on their way.
 */
const startsPendingWatch = (action, state) => {
  if (state.stockSync.watchedBy !== STOCK_SYNC_WATCHER.LISTINGS) return false;
  if (isAnyOf(syncListing.fulfilled, updateListing.fulfilled)(action)) return true;
  return (
    fetchListings.fulfilled.match(action) &&
    action.meta.arg?.background !== true &&
    hasPendingListings(state.stockSync.listings.rows)
  );
};

/**
 * The reactions of the stock sync screens. There is no socket for it: a run
 * in progress is read every `RUN_POLL_MS` until it ends, and then the store,
 * the table of the page that watches and a toast say what it left. A product
 * on its way to the store is read with its page until none is pending. Both
 * stop when the page goes or another store is chosen. A comparison expires on
 * the hour, with nobody touching anything.
 *
 * @param {Function} startListening
 */
export const registerStockSyncListeners = (startListening) => {
  Object.values(RUN_KIND).forEach((kind) => {
    startListening({ predicate: startsRunWatch(kind), effect: watchRun(kind) });
  });

  // Whatever told of the comparison last: its summary is always in the store's runs.
  startListening({
    matcher: isAnyOf(fetchStockSync.fulfilled, fetchStockPreview.fulfilled, startStockPreview.fulfilled),
    effect: async (action, { dispatch, getState, delay, cancelActiveListeners }) => {
      cancelActiveListeners();
      const report = getState().stockSync.detail?.runs?.[RUN_KIND.STOCK_PREVIEW];
      const token = previewTokenOf(report);
      const expiresAt = report?.result?.expires_at;
      if (!token || !expiresAt || report.applied_at) return;
      await delay(Math.max(0, Date.parse(expiresAt) - Date.now()));
      if (previewTokenOf(getState().stockSync.detail?.runs?.[RUN_KIND.STOCK_PREVIEW]) === token) {
        dispatch(stockPreviewExpired(token));
      }
    },
  });

  startListening({
    predicate: startsPendingWatch,
    effect: async (action, { dispatch, getState, delay, cancelActiveListeners }) => {
      cancelActiveListeners();
      const { selectedId } = getState().stockSync;
      const until = Date.now() + PENDING_WATCH_MAX_MS;

      while (Date.now() < until) {
        await delay(PENDING_POLL_MS);
        const state = getState();
        if (state.stockSync.watchedBy !== STOCK_SYNC_WATCHER.LISTINGS || state.stockSync.selectedId !== selectedId) {
          return;
        }
        await dispatch(fetchListings({ background: true }));
        if (!hasPendingListings(getState().stockSync.listings.rows)) break;
      }
      // The chips count what the pushes left.
      dispatch(fetchStockSync({ settingId: selectedId, background: true }));
    },
  });

  // A product paused or resumed moves the chips.
  startListening({
    actionCreator: updateListing.fulfilled,
    effect: (action, { dispatch }) => {
      dispatch(fetchStockSync({ settingId: action.meta.arg.settingId, background: true }));
    },
  });
};
